import type { ReactNode } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@lyder-talk/ui";
import { Button } from "@/components/ui/button";
import { Mascot, type MascotMood } from "@/components/mascot";

export interface MascotConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Runs when the user confirms. The dialog closes automatically. */
  onConfirm: () => void;
  title: ReactNode;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Lydi's expression — pick one that fits the moment. Defaults to "nervous". */
  mood?: MascotMood;
  /** Accessible label override for the mascot, describing the situation. */
  mascotAriaLabel?: string;
  /** Style the confirm button as a destructive (red) action. Defaults to false. */
  destructive?: boolean;
}

/**
 * Reusable confirmation dialog fronted by Lydi (the mascot). Use it anywhere a
 * yes/no confirmation benefits from a friendlier, on-brand presentation —
 * destructive removals, leaving a flow, discarding changes, etc. Closes when the
 * user clicks outside or presses Escape.
 *
 * For a specific recurring confirmation, wrap this with the fixed copy (see
 * `UnsaveWordDialog`) rather than repeating the strings at each call site.
 */
export const MascotConfirmDialog = ({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  mood = "nervous",
  mascotAriaLabel,
  destructive = false,
}: MascotConfirmDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent size="sm" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start gap-3 pr-6 text-left">
        <Mascot mood={mood} size="sm" className="shrink-0" ariaLabel={mascotAriaLabel} />
        <div className="min-w-0">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="mt-1.5">{description}</DialogDescription>
        </div>
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
        <DialogClose asChild>
          <Button variant="outline">{cancelLabel}</Button>
        </DialogClose>
        <Button
          variant={destructive ? "destructive" : "default"}
          onClick={() => {
            onConfirm();
            onOpenChange(false);
          }}
        >
          {confirmLabel}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
